import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Provider, connect } from 'react-redux';
import { Button, Tooltip } from 'antd';
import store from '../../../../../store';
import OrganisationMainSection from '../../../../../components/OrganisationMainSection/OrganisationMainSection';
import Alert from '../../../../../components/Alert/Alert';
import Download from '../../../../../components/Download/Download';
import PolicyPreview from '../scenes/PolicyPreview/PolicyPreview';
import Certificate from '../scenes/Certificate/Certificate';
import { download } from '../../../../../components/App/AppAction';
import { businessNameSelector } from '../../../OrganisationSelectors';
import { latestPolicySelector } from '../PolicySelectors';
import '../Policy.scss';

const POLICY_GUIDANCE = (
  <>
    <strong>What to do:</strong> open <strong>Create New</strong> to generate policy content, then{' '}
    <strong>Download</strong> a PDF or view the <strong>Certificate</strong> when you&apos;re ready.
  </>
);

const POLICY_DETAIL = (
  <p>
    Maintain your organisation&apos;s continuity policy programme here — generate versions, export PDF copies, or start
    again when governance changes.
  </p>
);

const PolicyDocument = ({ businessName, latestPolicy, create, download: downloadPdf }) => {
  /**
   * Both exports render the policy that is on screen, so with no policy there is nothing to export
   * — the PDF would come back as an empty preview. Disabled rather than hidden: the guidance above
   * names both buttons by name, so removing them would leave it describing controls that are not
   * there. The tooltip says what to do instead.
   */
  const hasPolicy = Boolean(latestPolicy);
  const noPolicyReason = 'Create a policy document first — there is nothing to export yet.';

  const onDownloadPolicy = () => {
    const content = renderToStaticMarkup(
      <Provider store={store}>
        <PolicyPreview />
      </Provider>,
    );
    return downloadPdf(content);
  };

  const onViewCertificate = () => {
    const content = renderToStaticMarkup(
      <Provider store={store}>
        <Certificate />
      </Provider>,
    );
    return downloadPdf(content);
  };

  const toolbarActions = (
    <>
      <Button type="default" className="organisation-main-section__toolbar-ghost policy-toolbar-btn" onClick={create}>
        Create New
      </Button>
      <Download fileName={`Policy_Program_${businessName.replace(/\s+/g, '_')}.pdf`} download={onDownloadPolicy}>
        {(downloadFile, downloading) => (
          <Tooltip title={hasPolicy ? '' : noPolicyReason}>
            <Button
              type="default"
              className="organisation-main-section__toolbar-ghost policy-toolbar-btn"
              icon="download"
              disabled={!hasPolicy}
              loading={downloading}
              onClick={downloadFile}
            >
              Download
            </Button>
          </Tooltip>
        )}
      </Download>
      <Download fileName={`Certificate_${businessName.replace(/\s+/g, '_')}.pdf`} download={onViewCertificate}>
        {(downloadFile, downloading) => (
          <Tooltip title={hasPolicy ? '' : noPolicyReason}>
            <Button
              type="primary"
              className="organisation-main-section__toolbar-primary policy-toolbar-btn"
              icon="safety"
              disabled={!hasPolicy}
              loading={downloading}
              onClick={downloadFile}
            >
              Certificate
            </Button>
          </Tooltip>
        )}
      </Download>
    </>
  );

  return (
    <OrganisationMainSection
      title="Business Continuity Policy"
      tooltipContent={POLICY_DETAIL}
      guidance={POLICY_GUIDANCE}
      toolbarActions={toolbarActions}
      flowPrev="/organization/strategic-areas"
      flowNext="/organization/plans"
      flowNextText="Go to Plans"
    >
      <div className="policy-document-surface-inner">
        {latestPolicy ? <PolicyPreview /> : <Alert message="No policy document, please create new." button={null} />}
      </div>
    </OrganisationMainSection>
  );
};

const mapStateToProps = (state) => ({
  businessName: businessNameSelector(state),
  latestPolicy: latestPolicySelector(state),
});

const mapDispatchToProps = { download };

export default connect(mapStateToProps, mapDispatchToProps)(PolicyDocument);
